import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardHeader,
  Field,
  Input,
  Spinner,
} from "@/components/primitives";
import { Icon, type IconName } from "@/components/Icon";
import { PageHeader } from "@/components/Layout";
import { api } from "@/lib/api";
import { ProviderCatalog } from "@/screens/ProviderCatalog";
import type { StatuspagePreview } from "@/lib/types";

type RssPreview = Awaited<ReturnType<typeof api.parseRss>>;

type Tab = "catalog" | "rss" | "statuspage" | "webhook";

export function AddService() {
  const [tab, setTab] = useState<Tab>("catalog");

  return (
    <>
      <PageHeader
        crumbs={["Status Watch", "Admin", "Services", "Add"]}
        title="Add a service to monitor"
        subtitle="Pick a verified provider from the catalog, or point us at a custom feed, status page, or webhook."
      />

      <div style={{ padding: "20px 32px 28px", maxWidth: 920 }}>
        <div
          role="tablist"
          aria-label="Source type"
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gap: 8,
            marginBottom: 18,
          }}
        >
          <SourceTab
            active={tab === "catalog"}
            onClick={() => setTab("catalog")}
            icon="list"
            title="Provider catalog"
            subtitle="Search verified providers. No URL to find."
          />
          <SourceTab
            active={tab === "rss"}
            onClick={() => setTab("rss")}
            icon="rss"
            title="Custom RSS / Atom"
            subtitle="We poll a feed URL you supply."
          />
          <SourceTab
            active={tab === "statuspage"}
            onClick={() => setTab("statuspage")}
            icon="globe"
            title="Custom Statuspage"
            subtitle="An Atlassian Statuspage site you supply."
          />
          <SourceTab
            active={tab === "webhook"}
            onClick={() => setTab("webhook")}
            icon="webhook"
            title="Webhook"
            subtitle="Provider pushes events to a unique URL we generate."
          />
        </div>

        {tab === "catalog" && <ProviderCatalog />}
        {tab === "rss" && <RssForm />}
        {tab === "statuspage" && <StatuspageForm />}
        {tab === "webhook" && <WebhookForm />}
      </div>
    </>
  );
}

function SourceTab({
  active,
  onClick,
  icon,
  title,
  subtitle,
}: {
  active: boolean;
  onClick: () => void;
  icon: IconName;
  title: string;
  subtitle: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      style={{
        border: "none",
        font: "inherit",
        color: "var(--foreground)",
        textAlign: "left",
        flex: 1,
        padding: 14,
        background: "var(--card)",
        borderRadius: 12,
        cursor: "pointer",
        boxShadow: active
          ? "0 0 0 2px var(--primary)"
          : "0 0 0 1px color-mix(in oklch, var(--foreground) 8%, transparent)",
        display: "flex",
        gap: 12,
        alignItems: "flex-start",
      }}
    >
      <div
        style={{
          width: 36,
          height: 36,
          borderRadius: 9,
          background: active ? "var(--primary)" : "var(--muted)",
          color: active
            ? "var(--primary-foreground)"
            : "var(--muted-foreground)",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon name={icon} size={18} />
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{title}</div>
        <div
          style={{
            fontSize: 12,
            color: "var(--muted-foreground)",
            marginTop: 2,
          }}
        >
          {subtitle}
        </div>
      </div>
      <div
        style={{
          marginLeft: "auto",
          width: 16,
          height: 16,
          borderRadius: "50%",
          background: active ? "var(--primary)" : "transparent",
          boxShadow: active
            ? "0 0 0 1px var(--primary), inset 0 0 0 3px var(--card)"
            : "0 0 0 1px var(--border)",
        }}
      />
    </button>
  );
}

function RssForm() {
  const navigate = useNavigate();
  const [url, setUrl] = useState("https://www.githubstatus.com/history.rss");
  const [parsing, setParsing] = useState(false);
  const [parsed, setParsed] = useState<RssPreview | null>(null);
  const [name, setName] = useState("");
  const [interval, setInterval] = useState("60");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onParse = async () => {
    setError(null);
    setParsing(true);
    setParsed(null);
    try {
      const result = await api.parseRss(url);
      setParsed(result);
      setName(result.title);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setParsing(false);
    }
  };

  const onSave = async () => {
    if (!parsed) return;
    setSaving(true);
    try {
      await api.createServiceRss({
        name,
        feedUrl: url,
        pollIntervalSeconds: Number(interval) || 60,
      });
      navigate("/admin/services");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="RSS feed"
        description="Most provider status pages publish an RSS or Atom feed."
      />
      <div
        style={{
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
      >
        <Field label="Feed URL" hint="Example: https://www.githubstatus.com/history.rss">
          <div style={{ display: "flex", gap: 8 }}>
            <Input
              value={url}
              onChange={setUrl}
              placeholder="https://…"
              style={{ flex: 1 }}
              mono
            />
            <Button variant="outline" onClick={onParse} disabled={parsing}>
              {parsing ? (
                <>
                  <Spinner />
                  Parsing…
                </>
              ) : (
                "Parse"
              )}
            </Button>
          </div>
        </Field>

        {error && (
          <div
            style={{
              fontSize: 12,
              color: "var(--destructive)",
              padding: 10,
              borderRadius: 8,
              background:
                "color-mix(in oklch, var(--destructive) 10%, transparent)",
            }}
          >
            {error}
          </div>
        )}

        {parsed && (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: 12,
                background: "color-mix(in oklch, var(--success) 6%, var(--card))",
                boxShadow:
                  "0 0 0 1px color-mix(in oklch, var(--success) 25%, transparent)",
                borderRadius: 10,
              }}
            >
              <span
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: 7,
                  background: "var(--success)",
                  color: "#fff",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Icon name="check" size={15} />
              </span>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                  Valid feed · {parsed.entries.length} entries
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--muted-foreground)",
                  }}
                >
                  channel title "{parsed.title}" · {parsed.componentCount}{" "}
                  components detected
                </div>
              </div>
              <span
                style={{
                  fontSize: 11,
                  color: "var(--muted-foreground)",
                  fontFamily: "var(--font-mono)",
                }}
              >
                {parsed.sizeKb} KB
              </span>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.4fr 1fr",
                gap: 14,
              }}
            >
              <Field
                label="Service name"
                hint="Detected from feed title — edit if needed"
              >
                <Input value={name} onChange={setName} />
              </Field>
              <Field
                label="Poll interval (seconds)"
                hint="Minimum 30s · provider's recommended cadence is 60s"
              >
                <Input value={interval} onChange={setInterval} mono />
              </Field>
            </div>

            <div>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
                Last {parsed.entries.length} entries preview
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0,
                  borderRadius: 10,
                  boxShadow: "0 0 0 1px var(--border)",
                  overflow: "hidden",
                }}
              >
                {parsed.entries.map((row, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "10px 14px",
                      borderTop: i ? "1px solid var(--border)" : "none",
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: "50%",
                        background:
                          row.sev === "major"
                            ? "var(--destructive)"
                            : "var(--warning)",
                      }}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {row.title}
                      </div>
                    </div>
                    <span
                      style={{
                        fontSize: 11,
                        color: "var(--muted-foreground)",
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      {row.date}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        color: "var(--success)",
                        fontWeight: 500,
                      }}
                    >
                      {row.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: 8,
            borderTop: "1px solid var(--border)",
            marginTop: 4,
          }}
        >
          <Button
            variant="ghost"
            onClick={() => navigate("/admin/services")}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            icon="check"
            disabled={!parsed || saving}
            onClick={onSave}
          >
            {saving ? "Saving…" : "Add service"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function StatuspageForm() {
  const navigate = useNavigate();
  const [url, setUrl] = useState("https://www.githubstatus.com");
  const [checking, setChecking] = useState(false);
  const [preview, setPreview] = useState<StatuspagePreview | null>(null);
  const [name, setName] = useState("");
  const [interval, setInterval] = useState("60");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const onCheck = async () => {
    setError(null);
    setChecking(true);
    setPreview(null);
    try {
      const result = await api.previewStatuspage(url);
      setPreview(result);
      setName(result.name || url.replace(/^https?:\/\//, ""));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setChecking(false);
    }
  };

  const onSave = async () => {
    if (!preview) return;
    setSaving(true);
    try {
      await api.createServiceStatuspage({
        name,
        feedUrl: url,
        pollIntervalSeconds: Number(interval) || 60,
      });
      navigate("/admin/services");
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader
        title="Statuspage JSON"
        description="Atlassian Statuspage sites expose a machine-readable summary. Paste the page URL — we derive the endpoints."
      />
      <div
        style={{ padding: 20, display: "flex", flexDirection: "column", gap: 18 }}
      >
        <Field
          label="Status page URL"
          hint="The page itself works: https://www.githubstatus.com"
        >
          <div style={{ display: "flex", gap: 8 }}>
            <Input
              value={url}
              onChange={setUrl}
              placeholder="https://…"
              style={{ flex: 1 }}
              mono
            />
            <Button variant="outline" onClick={onCheck} disabled={checking}>
              {checking ? (
                <>
                  <Spinner />
                  Checking…
                </>
              ) : (
                "Check"
              )}
            </Button>
          </div>
        </Field>

        {error && (
          <div
            role="alert"
            style={{
              fontSize: 12,
              color: "var(--destructive)",
              padding: 10,
              borderRadius: 8,
              background:
                "color-mix(in oklch, var(--destructive) 10%, transparent)",
            }}
          >
            {error}
          </div>
        )}

        {preview && (
          <>
            <div
              style={{
                padding: 12,
                borderRadius: 10,
                background:
                  "color-mix(in oklch, var(--success) 6%, var(--card))",
                boxShadow:
                  "0 0 0 1px color-mix(in oklch, var(--success) 25%, transparent)",
                fontSize: 12,
              }}
            >
              <div style={{ fontSize: 13, fontWeight: 600 }}>
                {preview.name || "Statuspage"} · {preview.state}
              </div>
              <div style={{ color: "var(--muted-foreground)", marginTop: 2 }}>
                {preview.summary} · {preview.components.length} components ·{" "}
                {preview.incidents.length} recent incidents
              </div>
              <code
                style={{
                  display: "block",
                  marginTop: 8,
                  fontSize: 11,
                  fontFamily: "var(--font-mono)",
                  color: "var(--muted-foreground)",
                }}
              >
                {preview.summaryUrl}
              </code>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1.4fr 1fr",
                gap: 14,
              }}
            >
              <Field label="Service name" hint="Detected from the page — edit if needed">
                <Input value={name} onChange={setName} />
              </Field>
              <Field label="Poll interval (seconds)" hint="Minimum 30s">
                <Input value={interval} onChange={setInterval} mono />
              </Field>
            </div>
          </>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: 8,
            borderTop: "1px solid var(--border)",
            marginTop: 4,
          }}
        >
          <Button variant="ghost" onClick={() => navigate("/admin/services")}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon="check"
            disabled={!preview || saving}
            onClick={onSave}
          >
            {saving ? "Saving…" : "Add service"}
          </Button>
        </div>
      </div>
    </Card>
  );
}

function WebhookForm() {
  const navigate = useNavigate();
  const [name, setName] = useState("Cloudflare");
  const [state, setState] = useState<"idle" | "waiting" | "verified">("idle");
  const [url, setUrl] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [reveal, setReveal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onGenerate = async () => {
    setError(null);
    try {
      const result = await api.createServiceWebhook({ name });
      setUrl(result.service.sourceUrl);
      setSecret(result.signingSecret);
      setState("waiting");
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const onCopy = () => {
    if (!url) return;
    void navigator.clipboard?.writeText(url).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Card>
      <CardHeader
        title="Webhook"
        description="We generate a unique URL the provider will POST to whenever their status changes."
      />
      <div
        style={{
          padding: 20,
          display: "flex",
          flexDirection: "column",
          gap: 18,
        }}
      >
        <Field label="Service name" hint="Display name shown across dashboards">
          <Input value={name} onChange={setName} />
        </Field>

        {error && (
          <div
            style={{
              fontSize: 12,
              color: "var(--destructive)",
              padding: 10,
              borderRadius: 8,
              background:
                "color-mix(in oklch, var(--destructive) 10%, transparent)",
            }}
          >
            {error}
          </div>
        )}

        {state === "idle" && (
          <div
            style={{
              padding: 18,
              borderRadius: 10,
              boxShadow: "0 0 0 1px var(--border)",
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 600 }}>
              Generate a unique inbound webhook URL
            </div>
            <div
              style={{
                fontSize: 12,
                color: "var(--muted-foreground)",
                lineHeight: 1.5,
              }}
            >
              We'll generate a URL and HMAC signing secret. Paste them into the
              provider's webhook settings, then send a test ping. We only accept
              events whose signature matches the secret.
            </div>
            <div>
              <Button
                variant="primary"
                icon="webhook"
                onClick={onGenerate}
                disabled={!name.trim()}
              >
                Generate webhook
              </Button>
            </div>
          </div>
        )}

        {state !== "idle" && url && secret && (
          <>
            <Field
              label="Inbound URL"
              hint="Paste this into the provider's webhook settings"
            >
              <div style={{ display: "flex", gap: 6 }}>
                <code
                  style={{
                    flex: 1,
                    height: 36,
                    padding: "0 12px",
                    display: "inline-flex",
                    alignItems: "center",
                    fontFamily: "var(--font-mono)",
                    fontSize: 12,
                    background: "var(--muted)",
                    borderRadius: 8,
                    color: "var(--foreground)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {url}
                </code>
                <Button
                  variant="outline"
                  icon={copied ? "check" : "copy"}
                  onClick={onCopy}
                >
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </Field>

            <Field
              label="Signing secret"
              hint="Used for HMAC-SHA256 signature verification — store securely, we won't show it again."
            >
              <div style={{ display: "flex", gap: 6 }}>
                <code
                  style={{
                    flex: 1,
                    height: 36,
                    padding: "0 12px",
                    display: "inline-flex",
                    alignItems: "center",
                    fontFamily: "var(--font-mono)",
                    fontSize: 12,
                    background: "var(--muted)",
                    borderRadius: 8,
                    color: "var(--foreground)",
                    letterSpacing: "0.04em",
                  }}
                >
                  {reveal ? secret : secret.replace(/.(?=.{4})/g, "•")}
                </code>
                <Button
                  variant="outline"
                  icon={reveal ? "x" : "copy"}
                  onClick={() => setReveal((r) => !r)}
                >
                  {reveal ? "Hide" : "Reveal"}
                </Button>
              </div>
            </Field>

            <VerificationStatus
              state={state}
              onVerify={() => setState("verified")}
            />
          </>
        )}

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            paddingTop: 8,
            borderTop: "1px solid var(--border)",
            marginTop: 4,
          }}
        >
          <Button variant="ghost" onClick={() => navigate("/admin/services")}>
            Cancel
          </Button>
          <Button
            variant="primary"
            icon="check"
            disabled={state !== "verified"}
            onClick={() => navigate("/admin/services")}
          >
            Done
          </Button>
        </div>
      </div>
    </Card>
  );
}

function VerificationStatus({
  state,
  onVerify,
}: {
  state: "waiting" | "verified";
  onVerify: () => void;
}) {
  const isWaiting = state === "waiting";
  const isVerified = state === "verified";
  const ringColor = isVerified ? "var(--success)" : "var(--warning)";
  const bg = isVerified
    ? "color-mix(in oklch, var(--success) 6%, var(--card))"
    : "color-mix(in oklch, var(--warning) 6%, var(--card))";

  return (
    <div
      style={{
        padding: 16,
        borderRadius: 10,
        background: bg,
        boxShadow: `0 0 0 1px color-mix(in oklch, ${ringColor} 25%, transparent)`,
        display: "flex",
        alignItems: "center",
        gap: 14,
      }}
    >
      <div style={{ position: "relative", width: 38, height: 38, flexShrink: 0 }}>
        {isWaiting && (
          <span
            style={{
              position: "absolute",
              inset: -4,
              borderRadius: "50%",
              border:
                "3px solid color-mix(in oklch, var(--warning) 40%, transparent)",
              borderTopColor: "var(--warning)",
              animation: "sw-spin 0.9s linear infinite",
            }}
          />
        )}
        <span
          style={{
            position: "absolute",
            inset: 0,
            borderRadius: "50%",
            background: isVerified
              ? "var(--success)"
              : "color-mix(in oklch, var(--warning) 18%, transparent)",
            color: isVerified ? "#fff" : "var(--warning)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Icon name={isVerified ? "check" : "clock"} size={18} />
        </span>
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>
          {isWaiting ? "Waiting for first ping…" : "Verified · receiving events"}
        </div>
        <div
          style={{
            fontSize: 12,
            color: "var(--muted-foreground)",
            marginTop: 3,
            lineHeight: 1.5,
          }}
        >
          {isWaiting
            ? "Configure the URL and secret in the provider's webhook settings, then trigger a test event."
            : "First valid event received · HMAC signature matched."}
        </div>
      </div>
      {isWaiting && (
        <Button variant="outline" size="sm" icon="zap" onClick={onVerify}>
          Mark as verified
        </Button>
      )}
      {isVerified && (
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 5,
            fontSize: 11,
            color: "var(--success)",
            fontWeight: 600,
          }}
        >
          <Icon name="shield" size={12} /> HMAC verified
        </span>
      )}
    </div>
  );
}
